// /UI/Dropzone/hooks/useFileUpload.js
import { useState, useCallback, useRef } from 'react';
// Adjust these import paths to match your project structure
import { UPLOAD_STATUS, MAX_RETRIES } from '../utils/constants';
import { getOrderId, updateHiddenFormFields } from '../utils/helpers';
import { presignedURL } from '../../../../controllers/presignedURL';
import { uploadImage } from '../../../../controllers/uploadImage';
import { getNewFile } from '../../../../helpers/getNewFile';

/**
 * Hook to handle file uploading functionality
 * @param {Object} param0 - Hook parameters
 * @param {Array} param0.files - Array of files to upload
 * @param {Function} param0.addNotification - Function to add notifications
 * @param {string} param0.token - Authentication token
 * @param {Function} param0.setIsComplete - Function to set completion state
 * @returns {Object} Upload state and methods
 */
export const useFileUpload = ({ files, addNotification, token, setIsComplete }) => {
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadRetries, setUploadRetries] = useState({});
  const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.IDLE);
  const [currentUploadingId, setCurrentUploadingId] = useState(null);
  const [mediaFiles, setMediaFiles] = useState({
    images: [],
    videos: [],
  });

  // Refs for scrolling
  const fileListRef = useRef(null);
  const fileItemRefs = useRef({});

  /**
   * Upload a single file with progress tracking
   */
  const uploadSingleFile = useCallback(
    async fileData => {
      if (!token) {
        throw new Error('Authentication token not available');
      }

      // Set current uploading file ID for auto-scroll
      setCurrentUploadingId(fileData.id);

      // Initialize progress for this file
      setUploadProgress(prev => ({
        ...prev,
        [fileData.id]: 0,
      }));

      const orderID = getOrderId();

      const { newFileName, fileType, newFile, isVideoFile } = await getNewFile(
        { meta: { name: fileData.name }, file: fileData.file },
        orderID
      );

      // Set up progress tracking
      const onProgress = progressEvent => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(prev => ({
          ...prev,
          [fileData.id]: percentCompleted,
        }));
      };

      // Get presigned URL
      const presignedUrl = await presignedURL(token, newFileName, fileType);

      if (!presignedUrl) {
        throw new Error('Failed to get presigned URL');
      }

      // Upload with progress
      const uploadResult = await uploadImage(presignedUrl, newFile, fileType, onProgress);

      if (!uploadResult) {
        throw new Error('Upload failed');
      }

      // Update media files arrays
      setMediaFiles(prevFiles => {
        if (isVideoFile) {
          return {
            ...prevFiles,
            videos: [...prevFiles.videos, newFileName],
          };
        }
        return {
          ...prevFiles,
          images: [...prevFiles.images, newFileName],
        };
      });

      setUploadProgress(prev => ({
        ...prev,
        [fileData.id]: 100,
      }));

      return true;
    },
    [token]
  );

  /**
   * Retry a failed upload
   */
  const retryUpload = useCallback(
    async fileData => {
      if (isUploading) return;

      const currentRetries = uploadRetries[fileData.id] || 0;

      if (currentRetries >= MAX_RETRIES) {
        addNotification('error', `Maximum retry attempts reached for ${fileData.name}`);
        return;
      }

      setUploadRetries(prev => ({
        ...prev,
        [fileData.id]: currentRetries + 1,
      }));

      setUploadProgress(prev => ({
        ...prev,
        [fileData.id]: 0,
      }));

      try {
        await uploadSingleFile(fileData);
        addNotification('success', `Successfully uploaded ${fileData.name}`);
      } catch (error) {
        console.error('Error retrying upload:', error);
        setUploadProgress(prev => ({
          ...prev,
          [fileData.id]: -1,
        }));
        addNotification('error', `Failed to upload ${fileData.name}: ${error.message}`);
      }
    },
    [isUploading, uploadRetries, uploadSingleFile, addNotification]
  );

  /**
   * Handles the upload of all files
   */
  const handleUpload = useCallback(async () => {
    if (files.length === 0) {
      addNotification('warning', 'Please add files before uploading.');
      return;
    }

    const videoCount = files.filter(file => file.isVideo).length;

    if (videoCount > 3) {
      // Using hardcoded 3 here since we're inside a callback
      addNotification(
        'error',
        `Only 3 videos are allowed. Please remove ${videoCount - 3} video(s).`
      );
      return;
    }

    setIsUploading(true);
    setUploadStatus(UPLOAD_STATUS.UPLOADING);
    let successCount = 0;
    let failCount = 0;

    // Process files in sequence to avoid overwhelming the server
    for (const fileData of files) {
      try {
        if (uploadProgress[fileData.id] === 100) {
          // Skip already uploaded files
          successCount++;
          continue;
        }

        await uploadSingleFile(fileData);
        successCount++;
      } catch (error) {
        console.error('Error uploading file:', error);
        setUploadProgress(prev => ({
          ...prev,
          [fileData.id]: -1, // Use -1 to indicate error
        }));
        failCount++;
      }
    }

    // Clear current uploading file ID
    setCurrentUploadingId(null);

    // Show final status
    if (successCount === files.length) {
      addNotification('success', 'All your multimedia files were successfully uploaded.');

      // Update hidden form fields
      updateHiddenFormFields(mediaFiles);

      setUploadStatus(UPLOAD_STATUS.COMPLETED);
      setIsComplete(true);
    } else if (successCount > 0) {
      addNotification('warning', `${successCount} of ${files.length} files uploaded successfully.`);
      setUploadStatus(UPLOAD_STATUS.PARTIAL);
    } else {
      addNotification('error', 'All uploads failed. Please try again.');
      setUploadStatus(UPLOAD_STATUS.FAILED);
    }

    setIsUploading(false);
  }, [files, uploadProgress, uploadSingleFile, addNotification, setIsComplete, mediaFiles]);

  /**
   * Reset upload state for a fresh upload session
   */
  const resetUploadState = useCallback(() => {
    setUploadProgress({});
    setUploadRetries({});
    setCurrentUploadingId(null);
    setUploadStatus(UPLOAD_STATUS.IDLE);
  }, []);

  return {
    uploadProgress,
    isUploading,
    uploadStatus,
    currentUploadingId,
    mediaFiles,
    fileListRef,
    fileItemRefs,
    handleUpload,
    retryUpload,
    resetUploadState,
  };
};
